import React, { useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Eraser,
  Check,
  ChevronDown,
  Calendar,
} from "lucide-react";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const STATUS_OPTIONS = [
  { value: "all", label: "Tất cả trạng thái" },
  { value: "open", label: "Đã gửi (Chờ xử lý)" },
  { value: "in_progress", label: "Đang xử lý" },
  { value: "resolved", label: "Đã xử lý" },
  { value: "closed", label: "Đã đóng" },
];

const TYPE_OPTIONS = [
  { value: "all", label: "Tất cả loại yêu cầu" },
  { value: "feedback", label: "Góp ý" },
  { value: "support", label: "Yêu cầu hỗ trợ" },
];

const CATEGORY_OPTIONS = [
  { value: "all", label: "Tất cả danh mục" },
  { value: "login", label: "Đăng nhập / thiết bị" },
  { value: "attendance", label: "Điểm danh" },
  { value: "schedule", label: "Thời khóa biểu" },
  { value: "materials", label: "Học liệu / bài giảng" },
  { value: "account", label: "Tài khoản" },
  { value: "other", label: "Khác" },
];

export default function TeacherSupportFilter({
  filters = {},
  onChange = () => {},
  onRefresh = () => {},
  totalRecords = 0,
}) {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [debouncedKeyword] = useDebounce(filters.keyword || "", 400);

  useEffect(() => {
    if (debouncedKeyword !== (filters.keyword || "")) {
      onChange({ ...filters, keyword: debouncedKeyword });
    }
  }, [debouncedKeyword]);

  const update = (key, value) => {
    onChange({ ...filters, [key]: value });
  };

  const isFiltering = Boolean(
    filters.keyword ||
      (filters.status && filters.status !== "all") ||
      (filters.request_type && filters.request_type !== "all") ||
      (filters.category && filters.category !== "all") ||
      filters.from_date ||
      filters.to_date
  );

  const clearFilters = () => {
    onChange({
      keyword: "",
      status: "all",
      request_type: "all",
      category: "all",
      from_date: "",
      to_date: "",
    });
  };

  const activeAdvancedCount = [
    filters.status && filters.status !== "all",
    filters.request_type && filters.request_type !== "all",
    filters.category && filters.category !== "all",
    filters.from_date,
    filters.to_date,
  ].filter(Boolean).length;

  const toolbarFieldClass =
    "h-11 rounded-2xl border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 outline-none transition-all focus:border-red-500 focus:bg-white focus:ring-0";

  const ActionButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-all duration-200 ${
        active
          ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
          : "border-gray-200 bg-gray-50 text-gray-500 hover:border-red-300 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, onChange, options }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value || "all"}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm font-medium shadow-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const DateBox = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <Calendar
          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
          size={16}
        />
        <input
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-gray-200 py-3 pl-11 pr-4 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all"
        />
      </div>
    </div>
  );

  const advancedFields = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <SelectBox
        label="Trạng thái"
        value={filters.status}
        onChange={(v) => update("status", v)}
        options={STATUS_OPTIONS}
      />
      <SelectBox
        label="Loại yêu cầu"
        value={filters.request_type}
        onChange={(v) => update("request_type", v)}
        options={TYPE_OPTIONS}
      />
      <SelectBox
        label="Danh mục"
        value={filters.category}
        onChange={(v) => update("category", v)}
        options={CATEGORY_OPTIONS}
      />
      <DateBox
        label="Từ ngày"
        value={filters.from_date}
        onChange={(v) => update("from_date", v)}
      />
      <DateBox
        label="Đến ngày"
        value={filters.to_date}
        onChange={(v) => update("to_date", v)}
      />
    </div>
  );

  /* ============================= MOBILE ============================= */
  if (isMobile) {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm GV, trường, SĐT, tiêu đề…"
              value={filters.keyword || ""}
              onChange={(e) => update("keyword", e.target.value)}
              className={`${toolbarFieldClass} w-full pl-11 pr-4`}
            />
          </div>

          <ActionButton icon={RotateCw} onClick={onRefresh} title="Làm mới" />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(true)}
            active={advanced || activeAdvancedCount > 0}
            title="Lọc nâng cao"
          />
        </div>

        {isFiltering && (
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500 px-1">
            <span>{totalRecords} kết quả phù hợp</span>
            <button
              type="button"
              onClick={clearFilters}
              className="font-bold text-red-600 flex items-center gap-1 uppercase tracking-wider"
            >
              <Eraser size={13} /> Xóa lọc
            </button>
          </div>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <>
              {isFiltering ? (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setAdvanced(false);
                  }}
                  className="flex-1 rounded-2xl border border-gray-300 py-4 font-bold text-gray-600 hover:bg-gray-50"
                >
                  Xóa lọc
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 py-4 font-bold text-white shadow-lg shadow-red-200"
              >
                <Check size={18} />
                Áp dụng
              </button>
            </>
          }
        >
          {advancedFields}
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  /* ============================= DESKTOP ============================= */
  return (
    <div className="mb-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <div className="relative min-w-[280px] max-w-md flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm theo giáo viên, trường học, SĐT, tiêu đề..."
              value={filters.keyword || ""}
              onChange={(e) => update("keyword", e.target.value)}
              className={`${toolbarFieldClass} w-full pl-11 pr-4`}
            />
          </div>

          <ActionButton icon={RotateCw} onClick={onRefresh} title="Làm mới dữ liệu" />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced || activeAdvancedCount > 0}
            title="Lọc nâng cao"
          />

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-red-500 transition-all hover:text-red-700"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        {totalRecords > 0 && (
          <div className="text-xs text-gray-400 font-medium shrink-0">
            Tổng cộng: <strong className="text-gray-700">{totalRecords}</strong> phiếu
          </div>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-5 pt-5 border-t border-gray-100">
              {advancedFields}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
